<!--
  COD7 · Provvigioni advisor — resoconto mensile.

  Stesso impianto di COD6 e COD8 (mese, KPI, segmenti «da liquidare /
  liquidati», tabella con azione di chiusura).

  ⚠️ DA NON CONFONDERE CON `/contabilita/cod7/:id`, che porta a `Cod4PageValet`
  (il *pagamento valet* della vecchia numerazione). Questa pagina sta su
  `/contabilita/cod7` (senza id) e riguarda
  le provvigioni degli advisor sui partner che seguono.

  Come nasce il numero (`AdvisorService`):
   - imponibile = SOMMA di `booking.imponibile` dei partner dell'advisor nel
     mese, calcolata con una SUM in SQL — non è una somma fatta qui;
   - provvigione = imponibile × `advisor.commission_percent`;
   - il ricalcolo NON tocca le righe già liquidate e riporta quante ne salta.

  La liquidazione, come nel COD8, **non è reversibile**: non esiste una rotta
  per annullarla, quindi non c'è un pulsante che finga di poterlo fare.
-->
<template>
  <div
    v-loading="loading"
    class="c7"
  >
    <div class="c7__head">
      <button
        type="button"
        class="c7__back"
        @click="goBack"
      >
        ‹
      </button>
      <div>
        <h1 class="c7__title">
          <span class="c7__code">COD7</span> Provvigioni advisor
        </h1>
        <p class="c7__sub">
          Provvigioni degli advisor sui partner che seguono — base mensile
        </p>
      </div>
    </div>

    <div class="c7__filters">
      <div class="field">
        <label>Mese</label>
        <div class="period">
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(-1)"
          >
            ‹
          </button>
          <span class="period__lbl">{{ periodLabel }}</span>
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(1)"
          >
            ›
          </button>
        </div>
      </div>
      <el-button
        :loading="recalculating"
        @click="recalculate"
      >
        Ricalcola periodo
      </el-button>
      <div class="c7__sp" />
      <el-input
        v-model="search"
        placeholder="Nome advisor…"
        clearable
        style="width: 210px"
      />
    </div>

    <div class="kpis">
      <div class="kpi">
        <div class="kpi__lbl">
          Provvigioni del mese
        </div>
        <div class="kpi__num">
          {{ euro(kpi.total) }}
        </div>
      </div>
      <div class="kpi kpi--attn">
        <div class="kpi__lbl">
          Da liquidare
        </div>
        <div class="kpi__num">
          {{ euro(kpi.toPay) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Liquidato
        </div>
        <div class="kpi__num">
          {{ euro(kpi.paid) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Advisor
        </div>
        <div class="kpi__num">
          {{ rows.length }}
        </div>
      </div>
    </div>

    <div class="board">
      <div class="toolbar">
        <div class="segs">
          <button
            v-for="s in SEGMENTS"
            :key="s.key"
            type="button"
            class="seg"
            :class="{ 'is-active': segment === s.key }"
            @click="segment = s.key"
          >
            {{ s.label }}
            <span class="seg__c">{{ countOf(s.key) }}</span>
          </button>
        </div>
      </div>

      <el-table
        :data="filtered"
        style="width: 100%"
        :empty-text="segment === 'daliq' && countOf('liq')
          ? `Niente da liquidare: ${countOf('liq')} ${countOf('liq') === 1 ? 'provvigione già liquidata' : 'provvigioni già liquidate'}, nella scheda Liquidate`
          : 'Nessuna provvigione per il periodo. Prova a ricalcolare il mese.'"
      >
        <el-table-column
          label="Advisor"
          min-width="230"
        >
          <template #default="scope">
            <div class="adv">
              <span class="ava">{{ ini(scope.row.advisor) }}</span>
              <div>
                <div class="adv__nm">
                  {{ advisorName(scope.row.advisor) }}
                </div>
                <div class="adv__sub">
                  {{ scope.row.advisor?.mail || '—' }}
                </div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Imponibile"
          width="150"
          align="right"
        >
          <template #default="scope">
            <span class="num">{{ euro(scope.row.taxable_amount) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="%"
          width="90"
          align="right"
        >
          <template #default="scope">
            <span class="num">
              {{ scope.row.advisor?.commission_percent != null
                ? scope.row.advisor.commission_percent + '%'
                : '—' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          label="Provvigione"
          width="150"
          align="right"
        >
          <template #default="scope">
            <span class="num val">{{ euro(scope.row.commission_amount) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Stato"
          min-width="180"
        >
          <template #default="scope">
            <StatusPill
              :kind="scope.row.liquidato ? 'ok' : 'attn'"
              :label="scope.row.liquidato ? 'Liquidata' : 'Da liquidare'"
            />
            <span
              v-if="scope.row.liquidato && scope.row.liquidation_date"
              class="c7__meta"
            >
              {{ fmtDate(scope.row.liquidation_date) }}
              <template v-if="scope.row.rif_fattura">
                · {{ scope.row.rif_fattura }}
              </template>
            </span>
          </template>
        </el-table-column>

        <el-table-column
          label="Azioni"
          width="150"
          align="right"
          fixed="right"
        >
          <template #default="scope">
            <el-button
              v-if="!scope.row.liquidato"
              size="small"
              type="success"
              :loading="liquidating === scope.row.id"
              @click.stop="confirmLiquidate(scope.row)"
            >
              Liquida
            </el-button>
            <span
              v-else
              class="muted"
            >—</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="board__foot">
        <span>{{ filtered.length }} advisor</span>
        <span class="c7__sp" />
        <span class="board__tot">
          Totale periodo <b>{{ euro(kpi.total) }}</b>
        </span>
      </div>
    </div>

    <p class="c7__note">
      L'imponibile è la somma dei booking dei partner seguiti dall'advisor nel
      mese; la provvigione è quella percentuale applicata all'imponibile. Il
      ricalcolo non modifica le righe già liquidate, e la liquidazione
      <b>non è reversibile</b> dall'applicazione.
    </p>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import contabilitaAPI from '../../../services/contabilitaAPI.js';
import { periodFromRoute, syncPeriodToUrl } from '../../../services/period.js';
import StatusPill from '../../../components/ui/StatusPill.vue';

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

const SEGMENTS = [
  { key: 'daliq', label: 'Da liquidare' },
  { key: 'liq', label: 'Liquidate' },
];

export default {
  name: 'Cod7Page',
  components: { StatusPill },

  setup() {
    const route = useRoute();
    const router = useRouter();
    // Il periodo arriva dalla URL (?y=&m=), non dal calendario: chi entra
    // dall'hub deve restare sul mese che stava guardando. Senza query valida
    // si ricade sul mese corrente.
    const periodo = periodFromRoute(route);
    const year = ref(periodo.year);
    const month = ref(periodo.month);

    const rows = ref([]);
    const loading = ref(false);
    const recalculating = ref(false);
    const liquidating = ref('');
    const search = ref('');
    const segment = ref('daliq');

    const periodLabel = computed(() => `${MONTHS[month.value - 1]} ${year.value}`);

    const advisorName = (a) =>
      [a?.name, a?.surname].filter(Boolean).join(' ') || '—';

    const bySegment = (key) =>
      rows.value.filter((r) => (key === 'liq' ? r.liquidato : !r.liquidato));

    const countOf = (key) => bySegment(key).length;

    const filtered = computed(() => {
      const q = search.value.trim().toLowerCase();
      return bySegment(segment.value).filter(
        (r) => !q || advisorName(r.advisor).toLowerCase().includes(q)
      );
    });

    const kpi = computed(() => {
      let total = 0;
      let toPay = 0;
      let paid = 0;
      for (const r of rows.value) {
        const v = Number(r.commission_amount || 0);
        total += v;
        if (r.liquidato) paid += v;
        else toPay += v;
      }
      return { total, toPay, paid };
    });

    const load = async () => {
      loading.value = true;
      try {
        rows.value = await contabilitaAPI.cod7Rows(year.value, month.value);
      } catch (e) {
        console.error('Caricamento provvigioni fallito:', e);
        rows.value = [];
      } finally {
        loading.value = false;
      }
    };

    // Il ricalcolo è sicuro: il backend salta le righe già liquidate. Vale la
    // pena dirlo nel messaggio, così si capisce perché un totale non cambia.
    const recalculate = async () => {
      recalculating.value = true;
      try {
        const res = await contabilitaAPI.cod7Recalculate(year.value, month.value);
        const skipped = res?.skipped_liquidated ?? 0;
        ElMessage.success(
          `Ricalcolate ${res?.computed ?? 0} provvigioni` +
            (skipped ? ` · ${skipped} già liquidate, lasciate invariate` : '')
        );
        await load();
      } catch (e) {
        console.error('Ricalcolo fallito:', e);
        ElMessage.error(
          e?.response?.status === 403
            ? 'Il ricalcolo è riservato a Simba'
            : 'Ricalcolo non riuscito'
        );
      } finally {
        recalculating.value = false;
      }
    };

    const confirmLiquidate = async (row) => {
      let rif;
      try {
        const { value } = await ElMessageBox.prompt(
          `Verrà liquidata la provvigione di ${advisorName(row.advisor)} per ` +
            `${euro(row.commission_amount)}. L'operazione non è reversibile ` +
            'dall\'applicazione. Puoi indicare il riferimento fattura (facoltativo).',
          'Confermi la liquidazione?',
          {
            confirmButtonText: 'Liquida',
            cancelButtonText: 'Annulla',
            inputPlaceholder: 'Rif. fattura (facoltativo)',
            inputValue: '',
            type: 'warning',
          }
        );
        rif = value;
      } catch {
        return; // annullato
      }

      liquidating.value = row.id;
      try {
        await contabilitaAPI.cod7Liquidate(row.id, rif || undefined);
        ElMessage.success('Provvigione liquidata');
        await load();
      } catch (e) {
        console.error('Liquidazione fallita:', e);
        // il backend risponde 409 se qualcuno l'ha già liquidata nel frattempo
        ElMessage.error(
          e?.response?.status === 409
            ? 'Questa provvigione risulta già liquidata: ricarico l\'elenco'
            : 'Liquidazione non riuscita'
        );
        if (e?.response?.status === 409) await load();
      } finally {
        liquidating.value = '';
      }
    };

    const shiftMonth = (delta) => {
      let m = month.value + delta;
      let y = year.value;
      if (m < 1) { m = 12; y -= 1; }
      if (m > 12) { m = 1; y += 1; }
      month.value = m;
      year.value = y;
      // la URL segue il mese mostrato: un refresh non riporta a oggi
      syncPeriodToUrl(router, route, y, m);
      load();
    };

    function euro(n) {
      return `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
    }

    const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('it-IT') : '');

    const ini = (a) =>
      `${(a?.name || '')[0] || ''}${(a?.surname || '')[0] || ''}`.toUpperCase() || '?';

    const goBack = () => router.push('/contabilita');

    onMounted(load);

    return {
      year, month, rows, loading, recalculating, liquidating, search, segment,
      SEGMENTS, filtered, kpi, periodLabel,
      countOf, recalculate, confirmLiquidate, shiftMonth,
      euro, fmtDate, ini, advisorName, goBack,
    };
  },
};
</script>

<style scoped>
.c7 { padding: 4px 0 32px; }
.c7__sp { flex: 1; }

.c7__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 0 2px 18px;
}
.c7__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; cursor: pointer;
  font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.c7__back:hover { border-color: var(--brand-violet); }
.c7__title {
  margin: 0; font-size: 21px; display: flex; align-items: center; gap: 10px;
  color: var(--text-primary);
}
.c7__code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px; font-weight: 700; color: #fff; background: #a62e82;
  padding: 3px 8px; border-radius: 6px;
}
.c7__sub { margin: 3px 0 0; font-size: 13px; color: var(--text-tertiary); }

.c7__filters {
  display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 14px;
}
.field {
  display: flex; align-items: center; gap: 8px; padding: 5px 11px;
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: 10px;
}
.field label {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 700;
}
.period { display: flex; align-items: center; gap: 2px; }
.period__nav {
  width: 30px; height: 30px; border: 0; background: transparent;
  border-radius: 7px; cursor: pointer; font-size: 15px;
  color: var(--text-secondary); font-family: inherit;
}
.period__nav:hover { background: var(--bg-tertiary); }
.period__lbl {
  padding: 0 8px; font-weight: 700; font-variant-numeric: tabular-nums;
  min-width: 118px; text-align: center; color: var(--text-primary);
}

.kpis {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px;
}
@media (max-width: 820px) { .kpis { grid-template-columns: repeat(2, 1fr); } }
.kpi {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); padding: 13px 15px;
}
.kpi__lbl {
  font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 600;
}
.kpi__num {
  font-size: 22px; font-weight: 750; letter-spacing: -0.02em; margin-top: 4px;
  font-variant-numeric: tabular-nums; color: var(--text-primary);
}
.kpi--attn .kpi__num { color: var(--state-attn); }

.board {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden;
}
.toolbar {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-bottom: 1px solid var(--border-color);
}
.segs { display: flex; gap: 5px; }
.seg {
  border: 1px solid transparent; background: transparent; cursor: pointer;
  color: var(--text-secondary); padding: 7px 12px; border-radius: 8px;
  font-size: 13px; font-weight: 600; font-family: inherit;
  display: inline-flex; align-items: center; gap: 8px;
}
.seg:hover { background: var(--bg-tertiary); }
.seg.is-active {
  background: var(--svc-fleet-bg); color: var(--brand-violet);
  border-color: var(--brand-violet);
}
.seg__c {
  font-size: 11.5px; font-weight: 700; border-radius: 20px; padding: 1px 8px;
  background: var(--state-muted-bg); color: var(--state-muted);
}
.seg.is-active .seg__c { background: var(--brand-violet); color: #fff; }

.adv { display: flex; align-items: center; gap: 9px; }
.ava {
  width: 28px; height: 28px; flex: none; border-radius: 50%;
  display: grid; place-items: center; font-size: 10.5px; font-weight: 700;
  color: #fff; background: var(--avatar-bg);
}
.adv__nm { font-weight: 600; color: var(--text-primary); }
.adv__sub { color: var(--text-tertiary); font-size: 11.5px; }

.num { font-variant-numeric: tabular-nums; }
.val { font-weight: 700; color: var(--text-primary); }
.muted { color: var(--text-tertiary); }
.c7__meta {
  display: block; margin-top: 3px; font-size: 11.5px; color: var(--text-tertiary);
}

.board__foot {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-top: 1px solid var(--border-color);
  font-size: 12.5px; color: var(--text-tertiary);
}
.board__tot { color: var(--text-secondary); }
.board__tot b {
  color: var(--text-primary); font-variant-numeric: tabular-nums; font-size: 14px;
}

.c7__note {
  margin: 12px 0 0; font-size: 12px; color: var(--text-tertiary); line-height: 1.5;
}

.c7 :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  padding: 10px 12px; border-bottom: 1px solid var(--border-color);
}
</style>
